// LAB-008 : durées lisibles pour les délais du circuit laboratoire (acheminement, rendu) et impression du registre.

export function duree_lisible(minutes) {
    if (minutes === null || minutes === undefined || minutes === '' || isNaN(Number(minutes))) return '—'
    const total = Math.max(0, Math.round(Number(minutes)))
    const jours = Math.floor(total / 1440), heures = Math.floor((total % 1440) / 60), reste = total % 60
    if (jours > 0) return jours + ' j ' + heures + ' h'
    if (heures > 0) return heures + ' h ' + (reste < 10 ? '0' : '') + reste
    return reste + ' min'
}

export function date_heure(value) {
    if (!value) return ''
    const morceaux = String(value).replace('T', ' ').split(' ')
    const date = morceaux[0].split('-')
    if (date.length !== 3) return String(value)
    const heure = morceaux.length > 1 ? morceaux[1].substr(0, 5) : ''
    return date[2] + '/' + date[1] + '/' + date[0] + (heure ? ' ' + heure : '')
}

// Horodatages SQL sans suffixe : le stockage utilise le fuseau applicatif.
// Le formatage emploie toujours le fuseau configuré de la clinique, jamais celui du navigateur.
export function date_heure_zone(value, zone = 'UTC', sourceZone = 'UTC') {
    if (!value) return ''
    const raw = String(value).trim().replace(' ', 'T')
    if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(raw)) return date_heure(value)
    let instant
    if (/Z$|[+-]\d{2}:?\d{2}$/.test(raw)) {
        instant = new Date(raw)
    } else {
        const base = Date.parse(raw + 'Z')
        if (Number.isNaN(base)) return date_heure(value)
        const formatter = new Intl.DateTimeFormat('en-GB', { timeZone: sourceZone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' })
        let guess = base
        // Deux passes couvrent les changements d'heure du fuseau source.
        for (let pass = 0; pass < 2; pass++) {
            const fields = Object.fromEntries(formatter.formatToParts(new Date(guess)).filter(part => part.type !== 'literal').map(part => [part.type, Number(part.value)]))
            const represented = Date.UTC(fields.year, fields.month - 1, fields.day, fields.hour, fields.minute, fields.second)
            guess += base - represented
        }
        instant = new Date(guess)
    }
    if (Number.isNaN(instant.getTime())) return date_heure(value)
    const fields = Object.fromEntries(new Intl.DateTimeFormat('fr-FR', { timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(instant).filter(part => part.type !== 'literal').map(part => [part.type, part.value]))
    return `${fields.day}/${fields.month}/${fields.year} ${fields.hour}:${fields.minute}`
}

export function mois_courant() {
    const now = new Date()
    const mois = now.getMonth() + 1
    return now.getFullYear() + '-' + (mois < 10 ? '0' : '') + mois
}

export function libelle_mois(mois) {
    const morceaux = String(mois || '').split('-')
    if (morceaux.length !== 2) return String(mois || '')
    const noms = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre']
    const index = Number(morceaux[1]) - 1
    return (noms[index] || morceaux[1]) + ' ' + morceaux[0]
}

const echapper = value => String(value === null || value === undefined ? '' : value).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]))

// Registre imprimable des non-conformités du mois (rejets d'échantillons et non-conformités déclarées)
export function registre_impression(mois, lignes, entete) {
    const e = echapper
    const zone = entete && entete.fuseau_horaire ? entete.fuseau_horaire : 'UTC'
    const rangs = (lignes || []).map(ligne => '<tr><td>' + e(date_heure_zone(ligne.occurred_at, zone)) + '</td><td>' + e(ligne.type === 'rejet' ? 'Rejet d’échantillon' : 'Non-conformité déclarée') + '</td><td>' +
        e([ligne.reference, ligne.patient, ligne.echantillon_code].filter(Boolean).join(' · ')) + '</td><td>' + e(ligne.motif_libelle || ligne.motif || '') + '</td><td>' + e(ligne.commentaire || '') + '</td><td>' + e(ligne.action || '') + '</td><td>' + e(ligne.acteur_nom || '') + '</td></tr>').join('')
    const nom = entete && entete.nom ? '<p><strong>' + e(entete.nom) + '</strong></p>' : ''
    return '<!doctype html><html lang="fr"><head><meta charset="utf-8"><title>Registre des non-conformités — ' + e(libelle_mois(mois)) + '</title><style>' +
        '@page { size: A4 landscape; margin: 12mm; } body { margin: 0; color: #35405c; font-family: Arial, sans-serif; font-size: 10pt; line-height: 1.4; } h1 { font-size: 15pt; margin: 0 0 3mm; }' +
        'table { width: 100%; border-collapse: collapse; margin-top: 4mm; } th, td { padding: 2mm; border: 0.2mm solid #d4dbea; text-align: left; vertical-align: top; white-space: pre-wrap; overflow-wrap: anywhere; } th { background: #f4f6fc; } tr { break-inside: avoid; }' +
        '.mention { color: #59677e; font-size: 9pt; }</style></head><body>' + nom + '<h1>Registre des non-conformités du laboratoire — ' + e(libelle_mois(mois)) + '</h1>' +
        '<p class="mention">' + e((lignes || []).length) + ' enregistrement(s) · Édité le ' + e(date_heure_zone(new Date().toISOString(), zone)) + '</p>' +
        '<table><thead><tr><th>Date</th><th>Type</th><th>Bon · patient · échantillon</th><th>Motif</th><th>Commentaire</th><th>Action</th><th>Déclaré par</th></tr></thead><tbody>' + (rangs || '<tr><td colspan="7">Aucune non-conformité sur la période.</td></tr>') + '</tbody></table></body></html>'
}
